import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { FormHelperText, RadioGroup } from '@mui/material';

import { IUnregisteredStepFiveB } from '../unregistered-steps-types';
import { isValidNumberOfPassengers } from '../../../../../utils/validation.util';
import { hideModal } from '../../../../../store/slices/generalSlice';
import { RootState } from '../../../../../store/rootReducer';
import { FormLayout } from '../../../../../global-styles/layout';
import { getErrorMessage } from '../../../../../constants/errorMessages';
import { BodySmallText, Heading4Text } from '../../../../../components/typography/typography';
import Radio from '../../../../../components/radio/radio';
import ModalContainer from '../../../../../components/common/modal-container/modal-container';
import ControlledInput from '../../../../../components/common/forms/ControlledInput';
import ControlledCounter from '../../../../../components/common/forms/ControlledCounter';
import CardSubsection from '../../../../../components/card-subsection/card-subsection';
import { UnregisteredStep5Style } from './unregistered-5-style';

const MIN_ADULTS = 0;

const Unregistered5b: React.FC<IUnregisteredStepFiveB> = ({ form }) => {
  const { t } = useTranslation(['common', 'auth']);

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    unregister,
    setFocus,
  } = form;

  const [adults, minors, passengersWithSpecialNeeds] = watch([
    'adults',
    'minors',
    'passengersWithSpecialNeeds',
    'generateMediaTask',
  ]);

  const modal = useSelector((state: RootState) => state.general.modal);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!passengersWithSpecialNeeds) {
      unregister('passengersInformation');
    }

    if (adults < MIN_ADULTS) {
      setValue('adults', MIN_ADULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengersWithSpecialNeeds, adults]);

  return (
    <UnregisteredStep5Style>
      <Heading4Text className="m-12px" weight={600}>
        {t('auth:unregistered.step-4.people-number-title')}
      </Heading4Text>
      <BodySmallText className="m-24px"> {t('auth:unregistered.step-4.people-number-text')}</BodySmallText>

      <FormLayout className="m-24px">
        <ControlledCounter
          counterProps={{
            label: t('auth:unregistered.step-4.adults'),
            maxCount: 9,
            minCount: MIN_ADULTS,
            dataCy: 'adults-in-car',
          }}
          controllerProps={{
            name: 'adults',
            control,
            rules: {
              required: true,
              validate: {
                isValidNumberOfPassengers: (adults: number) => isValidNumberOfPassengers(adults, minors),
              },
            },
          }}
        />

        <ControlledCounter
          counterProps={{
            label: t('auth:unregistered.step-4.kids'),
            maxCount: 9,
            minCount: 0,
            dataCy: 'minors-in-car',
          }}
          controllerProps={{
            name: 'minors',
            control,
            rules: {
              validate: {
                isValidNumberOfPassengers: (minors: number) => isValidNumberOfPassengers(adults, minors),
              },
            },
          }}
        />
        <FormHelperText className="error-block" error>
          {getErrorMessage(t, errors.adults?.type)}
        </FormHelperText>
      </FormLayout>

      <CardSubsection className="m-24px" label={t('auth:unregistered.step-4.special-needs')}>
        <RadioGroup aria-label="gender" name="gender1">
          <Radio
            id={'passengersWithSpecialNeeds-1'}
            dataCy={'passengersWithSpecialNeeds-1'}
            active={passengersWithSpecialNeeds === true}
            label={t('common:label-inputs.affirmative-response')}
            value="true"
            onClick={(e) => {
              setValue('passengersWithSpecialNeeds', e.currentTarget.checked);
              setFocus('passengersInformation' as any);
            }}
          />

          <Radio
            dataCy={'passengersWithSpecialNeeds-2'}
            id={'passengersWithSpecialNeeds-2'}
            active={passengersWithSpecialNeeds === false}
            label={t('common:label-inputs.negative-response')}
            value="false"
            onClick={(e) => {
              setValue('passengersWithSpecialNeeds', !e.currentTarget.checked);
            }}
          />
        </RadioGroup>
        <FormHelperText className="error-block" error>
          {getErrorMessage(t, errors.passengersWithSpecialNeeds?.type)}
        </FormHelperText>
      </CardSubsection>

      <ControlledInput
        inputProps={{
          rows: 2,
          multiline: true,
          variant: 'filled',
          placeholder: t('auth:unregistered.step-4.passangers-information-placeholder'),
          'data-cy': 'passengersInformation',
          inputProps: {
            maxLength: 255,
          },
        }}
        controllerProps={{
          control,
          name: 'passengersInformation',
        }}
      />

      {/* ModalConfirm Acepta generar la tarea de medios */}
      <ModalContainer
        fullScreen
        buttons={[
          {
            className: 'primary',
            onClick: () => {
              setValue('generateMediaTask', true);
              if (modal.onConfirm) modal.onConfirm(true);
            },
            label: t('common:label-inputs.affirmative-response'),
            dataCy: 'confirm-media-task-btn',
          },
          {
            className: 'secondary',
            onClick: () => {
              setValue('generateMediaTask', false);
              if (modal.onConfirm) modal.onConfirm(false);
            },
            label: t('common:label-inputs.negative-response'),
            dataCy: 'confirm-no-media-task-button',
          },
          {
            className: 'secondary',
            onClick: () => {
              dispatch(hideModal());
            },
            label: t('common:buttons.cancel'),
            dataCy: 'confirm-cancel-media-task-button',
          },
        ]}
        active={modal.name === 'confirm-media-task'}
        onClose={() => {
          dispatch(hideModal());
        }}
        title={t('auth:unregistered.step-4.modal-media-title')}
        text={t('auth:unregistered.step-4.modal-media-text')}
        dataCy="confirm-media-task-modal"
      ></ModalContainer>
    </UnregisteredStep5Style>
  );
};

export default Unregistered5b;
